import { supabase } from '../../client';
import { logError } from '@/lib/sentry';
import * as repo from '../repository/messageTemplateRepository';
import type {
  MessageTemplate,
  CreateMessageTemplateInput,
  UpdateMessageTemplateInput,
} from '@/types/messageTemplate';

export const getMessageTemplates = async (userId: string): Promise<MessageTemplate[]> => {
  return repo.getMessageTemplates(userId);
};

export const createMessageTemplate = async (
  userId: string,
  input: CreateMessageTemplateInput,
): Promise<MessageTemplate> => {
  return repo.createMessageTemplate(userId, input);
};

export const updateMessageTemplate = async (
  userId: string,
  templateId: number,
  updates: UpdateMessageTemplateInput,
): Promise<MessageTemplate> => {
  return repo.updateMessageTemplate(userId, templateId, updates);
};

export const deleteMessageTemplate = async (userId: string, templateId: number): Promise<void> => {
  return repo.deleteMessageTemplate(userId, templateId);
};

/**
 * 활성 템플릿의 content 변경 시 옛 컬럼(member_setting.message_template)에도
 * 같은 값을 미러링한다 (롤백 안전망).
 *
 * 사용자의 가장 최근 member_setting row를 update 한다 (없으면 no-op).
 * 실패해도 신 테이블 변경은 이미 성공했으므로 throw 하지 않고 logError만.
 */
export const mirrorActiveTemplateToLegacyColumn = async (
  userId: string,
  content: string,
): Promise<void> => {
  try {
    const { data: latest, error: fetchError } = await supabase
      .from('member_setting')
      .select('id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (fetchError) {
      logError('Failed to fetch latest member_setting for dual-write', fetchError);
      return;
    }
    if (!latest) return; // member_setting 없는 사용자 (lazy 생성된 케이스): 미러링 스킵

    const { error: updateError } = await supabase
      .from('member_setting')
      .update({ message_template: content })
      .eq('id', latest.id)
      .eq('user_id', userId);

    if (updateError) {
      logError('Failed to mirror active template to legacy column', updateError);
    }
  } catch (e) {
    logError('Unexpected error in mirrorActiveTemplateToLegacyColumn', e);
  }
};
